import { CombinedGraphQLErrors } from "@apollo/client";
import type { User } from "firebase/auth";
import { bodyErrorsOf, entriesFrom, graphQLErrorsOf, hasCode } from "../../../../shared/graphql/errorEntries";
import { getCoreApolloClient } from "../lib/apolloClient";
// The public client, for the Terms read only (see readSignupTerms).
import { getApolloClient } from "../lib/apolloClient";
import { readBookingTerms, termsReadFrom, type TermsRead } from "../features/terms/operations";
import type { TermsDecision, TermsLocale } from "../features/terms/terms";
import type { ClientAccount } from "./IdentityProvider";
import {
  GET_CLIENT_ACCOUNT,
  CREATE_CLIENT_ACCOUNT,
  CREATE_CLIENT_ACCOUNT_WITHOUT_TERMS,
  GET_CLIENT_ACCOUNT_CREATION_STATUS,
  CLIENT_SIGNUP_TERMS,
  REPAIR_CLIENT_ACCOUNT_EMAIL,
} from "./operations";

// The account documents live in ./operations.ts (c4). Re-exported for the
// callers and tests that import them from here.
export {
  GET_CLIENT_ACCOUNT,
  CREATE_CLIENT_ACCOUNT,
  CREATE_CLIENT_ACCOUNT_WITHOUT_TERMS,
  GET_CLIENT_ACCOUNT_CREATION_STATUS,
  CLIENT_SIGNUP_TERMS,
  REPAIR_CLIENT_ACCOUNT_EMAIL,
} from "./operations";

/**
 * THE TERMS ACCOUNT CREATION SHOWS, read where the reader can be answered.
 *
 * client-v1 `account.currentTerms` is on /api/v1/core, which needs a Firebase
 * ID token. The account form is often reached with NO user at all (an
 * account-only professional gates before any hold, so no anonymous identity
 * exists yet), and core would refuse that read, leaving "Create account"
 * disabled for everyone it is meant for. So: a credentialed (non-anonymous)
 * user reads client-v1 on core, as Backend designed for signup; anyone else
 * reads booking-v1's public `currentTerms`. Both are one server function
 * (`readCurrentTerms`) over one record, so the echo `account.create` receives
 * is the same either way.
 */
export async function readSignupTerms(user: User | null, locale: TermsLocale): Promise<TermsRead> {
  if (!user || user.isAnonymous) return readBookingTerms(getApolloClient(), locale);
  try {
    // Its own document: a core without client-v1 1.2 refuses only this read,
    // and that one refusal (the field absent) means no Terms here.
    const result = await getCoreApolloClient().query<{ account?: { currentTerms?: unknown } | null }>({
      query: CLIENT_SIGNUP_TERMS,
      variables: { locale },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
    return termsReadFrom(result, (data) => {
      const account = (data as { account?: { currentTerms?: unknown } | null }).account;
      // No `account` at all is a broken answer, not "none published".
      return account ? account.currentTerms : undefined;
    });
  } catch (thrown) {
    return termsReadFrom({ thrown }, () => undefined);
  }
}

export type AccountCreationStatus =
  | "CREATE_ALLOWED"
  | "RESUME_ALLOWED"
  | "COMPLETE_REFRESH_TOKEN"
  | "RESTORE_REQUIRED"
  | "SUPPORT_REQUIRED";

class ClientAccountSetupNeeded extends Error {
  constructor(readonly reason: "missing" | "contact") {
    super("Client account setup is required.");
  }
}

interface AccountData {
  account: {
    self: {
      uid: string;
      profile: {
        firstName: string | null;
        lastName: string | null;
        email: string | null;
      } | null;
    } | null;
  } | null;
}

export function isAccountSelfForbidden(error: unknown): boolean {
  return hasCode(error, "FORBIDDEN");
}

/** `["account", "self"]`: the refusal is account.self's own, not another field's. */
function isAccountSelfPath(path: unknown): boolean {
  return Array.isArray(path) && path.length === 2 && path[0] === "account" && path[1] === "self";
}

/**
 * account.self's refusals that send the client to ClientAccountCreationStatus
 * rather than to "we couldn't finish setting up your account".
 *
 * - FORBIDDEN (client-v1 README): an identity without the USER role may not
 *   read account.self; creationStatus decides what it may do.
 * - USER_NOT_FOUND, HTTP 404, path account.self (client-v1 1.6.0; Backend's
 *   rc.3 capture, 2026-10-07): the token carries the role, the user document
 *   is gone. Thrown since before rc.3; the Dev data reset surfaced it.
 *
 * READ FROM BOTH APOLLO WIRES. A GraphQL-typed non-2xx arrives as
 * CombinedGraphQLErrors; an application/json non-2xx as a ServerError with
 * only its body text. A ServerError body counts ONLY when its entry names
 * account.self as its path, so a gateway's body (no GraphQL path) never does.
 * Neither code grants anything here: creationStatus is authoritative, and
 * SUPPORT_REQUIRED, RESTORE_REQUIRED and unknown values still stop.
 */
export function selfRefusalNeedsCreationStatus(error: unknown): boolean {
  if (isAccountSelfForbidden(error)) return true;
  const typed = graphQLErrorsOf(error).some(
    (entry) => entry.extensions?.code === "USER_NOT_FOUND" && isAccountSelfPath(entry.path)
  );
  if (typed) return true;
  const body = bodyErrorsOf(error);
  if (!Array.isArray(body)) return false;
  return body.some((entry: unknown) => {
    if (!entry || typeof entry !== "object") return false;
    const { extensions, path } = entry as { extensions?: { code?: unknown }; path?: unknown };
    return (extensions?.code === "FORBIDDEN" || extensions?.code === "USER_NOT_FOUND") && isAccountSelfPath(path);
  });
}

export async function readCreationStatus(user: User): Promise<AccountCreationStatus> {
  if (user.isAnonymous || !user.email || !user.emailVerified) {
    throw new Error("A verified credentialed email is required.");
  }
  await user.getIdToken(true);
  const { data, error } = await getCoreApolloClient().query<{
    account: { creationStatus: unknown } | null;
  }>({
    query: GET_CLIENT_ACCOUNT_CREATION_STATUS,
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  if (error) throw error;
  const status = data?.account?.creationStatus;
  if (
    status === "CREATE_ALLOWED" || status === "RESUME_ALLOWED" ||
    status === "COMPLETE_REFRESH_TOKEN" || status === "RESTORE_REQUIRED" ||
    status === "SUPPORT_REQUIRED"
  ) return status;
  throw new Error("Unrecognised client account creation status.");
}

async function readSelf(): Promise<AccountData["account"]> {
  const { data, error } = await getCoreApolloClient().query<AccountData>({
    query: GET_CLIENT_ACCOUNT,
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  if (error) throw error;
  return data?.account ?? null;
}

export async function fetchClientAccount(user: User): Promise<ClientAccount> {
  if (user.isAnonymous || !user.email || !user.emailVerified) {
    throw new Error("A verified credentialed email is required.");
  }
  const self = (await readSelf())?.self;
  const profile = self?.profile;
  if (!self) throw new ClientAccountSetupNeeded("missing");
  if (self.uid !== user.uid) throw new Error("Client account UID mismatch.");
  const email = profile?.email?.trim() ?? "";
  const name = [profile?.firstName?.trim(), profile?.lastName?.trim()]
    .filter(Boolean)
    .join(" ");
  if (!name) throw new Error("Client account has no verified booking name.");
  if (!email || email.toLowerCase() !== user.email.toLowerCase()) {
    throw new ClientAccountSetupNeeded("contact");
  }
  return { name, email };
}

export type ClientAccountResolution =
  | { kind: "account"; account: ClientAccount }
  | { kind: "setup" }
  | { kind: "blocked" };

export async function resolveClientAccount(user: User): Promise<ClientAccountResolution> {
  try {
    return { kind: "account", account: await fetchClientAccount(user) };
  } catch (error) {
    if (!(error instanceof ClientAccountSetupNeeded) && !selfRefusalNeedsCreationStatus(error)) {
      return { kind: "blocked" };
    }
    try {
      const status = await readCreationStatus(user);
      if (status === "CREATE_ALLOWED" || status === "RESUME_ALLOWED") return { kind: "setup" };
      if (status === "COMPLETE_REFRESH_TOKEN") {
        try {
          return { kind: "account", account: await fetchClientAccount(user) };
        } catch (recheckError) {
          // A roleful account whose stored contact is stale can use the
          // verified-email update. No creation is permitted in this branch.
          return recheckError instanceof ClientAccountSetupNeeded && recheckError.reason === "contact"
            ? { kind: "setup" }
            : { kind: "blocked" };
        }
      }
      return { kind: "blocked" };
    } catch {
      return { kind: "blocked" };
    }
  }
}

/**
 * ANOTHER CREATE FOR THIS ACCOUNT IS STILL RUNNING (terms-consent-v1 README;
 * client-v1's next version): CONFLICT 409, detailCode
 * ACCOUNT_CREATE_IN_PROGRESS, retryable. Backend serialises two creates for
 * one uid (a second tab, a double tap); the loser waits about 3 s, then
 * answers this. The account was waited for and never appeared: the person
 * may try again, which reads the account first and never creates twice.
 */
export class AccountCreateInProgress extends Error {
  constructor() {
    super("Another account creation for this account is still running.");
  }
}

/** CONFLICT 409, detailCode ACCOUNT_CREATE_SUPPORT_REQUIRED, not retryable: the support path. */
export class AccountCreateSupportRequired extends Error {
  constructor() {
    super("Account creation needs support.");
  }
}

/**
 * Backend's two account-creation conflicts, read on BOTH wires: a
 * GraphQL-typed answer (CombinedGraphQLErrors) and an application/json
 * non-2xx (ServerError's body), since which one arrives depends on the
 * request's Accept and the server's negotiation.
 */
export function accountCreateConflictFrom(error: unknown): "in-progress" | "support-required" | null {
  for (const { code, ext } of entriesFrom(error)) {
    if (code !== "CONFLICT") continue;
    if (ext.detailCode === "ACCOUNT_CREATE_IN_PROGRESS") return "in-progress";
    if (ext.detailCode === "ACCOUNT_CREATE_SUPPORT_REQUIRED") return "support-required";
  }
  return null;
}

/** The bounded waits after ACCOUNT_CREATE_IN_PROGRESS: three re-reads, about 7.5 s in all. */
export const ACCOUNT_CREATE_WAITS_MS = Object.freeze([1500, 3000, 3000]);

/**
 * Wait for the account the other create is making: after each wait, ONE
 * token refresh (the role arrives in the token) and one read of
 * account.self. Never another create. Resolves once the account is
 * readable; throws AccountCreateInProgress when it never is.
 */
async function waitForAccountCreatedElsewhere(user: User): Promise<void> {
  for (const ms of ACCOUNT_CREATE_WAITS_MS) {
    await new Promise((resolve) => setTimeout(resolve, ms));
    await user.getIdToken(true);
    try {
      const self = (await readSelf())?.self;
      if (self && self.uid === user.uid) return;
    } catch (error) {
      // Not readable YET (no USER role, or the user document not yet
      // written): wait again. Anything else is not evidence either way.
      if (!selfRefusalNeedsCreationStatus(error)) throw error;
    }
  }
  throw new AccountCreateInProgress();
}

export async function createClientAccount(
  user: User,
  firstName: string,
  lastName: string,
  /**
   * What the person agreed to on the account form. Sent as `termsAcceptance`
   * when Terms were published; omitted when none were. Backend refuses a
   * first create without it once Terms are published (client-v1 1.2+).
   */
  terms: TermsDecision
): Promise<void> {
  const email = user.email?.trim() ?? "";
  if (user.isAnonymous || !user.emailVerified || !email || !firstName.trim() || !lastName.trim()) {
    throw new Error("Verified email and both names are required.");
  }
  // Refresh after the email-action reload: the backend reads the verified
  // email from this token, never from a client-supplied profile field.
  await user.getIdToken(true);
  let self: NonNullable<AccountData["account"]>["self"] = null;
  try {
    self = (await readSelf())?.self ?? null;
  } catch (error) {
    // An account without USER permission may not read account.self yet, and
    // one whose user document is gone answers USER_NOT_FOUND; creationStatus
    // decides both. Network faults and every other refusal are not evidence
    // of absence.
    if (!selfRefusalNeedsCreationStatus(error)) throw error;
  }
  if (self && self.uid !== user.uid) throw new Error("Client account UID mismatch.");

  if (!self) {
    const status = await readCreationStatus(user);
    if (status === "COMPLETE_REFRESH_TOKEN") {
      self = (await readSelf())?.self ?? null;
      if (!self || self.uid !== user.uid) throw new Error("Client account could not be reread.");
    } else if (status !== "CREATE_ALLOWED" && status !== "RESUME_ALLOWED") {
      // RESTORE_REQUIRED, SUPPORT_REQUIRED and future enum values must not
      // acquire create authority through a fallback.
      throw new Error("Client account setup requires support.");
    }
  }

  if (!self) {
    let created: { uid: string; profile: { email: string | null } | null } | null | undefined;
    let createdElsewhere = false;
    try {
      const { data } = await getCoreApolloClient().mutate<{
        account: { create: { uid: string; profile: { email: string | null } | null } | null } | null;
      }>({
        // WITH an acceptance, the 1.3 document; WITHOUT one, the 1.1 document,
        // which a runtime older than client-v1 1.2 still accepts (declaring
        // `$termsAcceptance` alone makes such a runtime refuse the document).
        ...(terms.acceptance
          ? {
              mutation: CREATE_CLIENT_ACCOUNT,
              variables: { firstName: firstName.trim(), lastName: lastName.trim(), email, termsAcceptance: terms.acceptance },
            }
          : {
              mutation: CREATE_CLIENT_ACCOUNT_WITHOUT_TERMS,
              variables: { firstName: firstName.trim(), lastName: lastName.trim(), email },
            }),
      });
      created = data?.account?.create;
    } catch (error) {
      const conflict = accountCreateConflictFrom(error);
      if (conflict === "support-required") throw new AccountCreateSupportRequired();
      if (conflict === "in-progress") {
        await waitForAccountCreatedElsewhere(user);
        createdElsewhere = true;
      } else {
        // A transport failure may have lost the response after create committed.
        // First ask the server for status; never replay on an ambiguous answer.
        if (CombinedGraphQLErrors.is(error)) throw error;
        try {
          if (await readCreationStatus(user) === "COMPLETE_REFRESH_TOKEN") {
            await fetchClientAccount(user);
            return;
          }
        } catch {
          // Preserve the original ambiguous failure for setup/support recovery.
        }
        throw error;
      }
    }
    if (!createdElsewhere) {
      if (created?.uid !== user.uid) {
        throw new Error("Client account creation could not be confirmed.");
      }
      await user.getIdToken(true);
    }
  }

  const afterCreate = (await readSelf())?.self;
  if (afterCreate?.uid !== user.uid) throw new Error("Client account could not be reread.");
  if (![afterCreate.profile?.firstName?.trim(), afterCreate.profile?.lastName?.trim()].some(Boolean)) {
    throw new Error("Client account has no booking name.");
  }
  if (afterCreate.profile?.email?.trim().toLowerCase() === email.toLowerCase()) {
    await fetchClientAccount(user);
    return;
  }
  // A readable USER account may have a missing or stale booking email. The
  // update accepts only this token's exact verified address.
  const { data: repaired } = await getCoreApolloClient().mutate<{
    account: { update: { uid: string; profile: { email: string | null } | null } | null } | null;
  }>({
    mutation: REPAIR_CLIENT_ACCOUNT_EMAIL,
    variables: { email },
  });
  const updated = repaired?.account?.update;
  if (updated?.uid !== user.uid || updated.profile?.email?.toLowerCase() !== email.toLowerCase()) {
    throw new Error("Client account email repair could not be confirmed.");
  }
  await user.getIdToken(true);
  await fetchClientAccount(user);
}
